'use client';

import { useCalendarStore } from '@/stores/calendar-store';
import { uiFb } from '@/lib/i18n';

interface YearSelectorProps {
  availableYears: number[];
  /** When true, removes "Year:" label and outer border (dock provides surface) */
  bare?: boolean;
}

export function YearSelector({ availableYears, bare }: YearSelectorProps) {
  const year = useCalendarStore(s => s.year);
  const setMonth = useCalendarStore(s => s.setMonth);
  const month = useCalendarStore(s => s.month);
  const lang = useCalendarStore(s => s.lang);

  const yearIdx = availableYears.indexOf(year);
  // If current year isn't in available list, find nearest
  const effectiveIdx = yearIdx >= 0 ? yearIdx : 0;
  const canPrev = effectiveIdx > 0;
  const canNext = effectiveIdx < availableYears.length - 1;

  if (availableYears.length <= 1) return null;

  return (
    <div className={bare ? 'flex items-center gap-0' : 'flex items-center justify-center gap-1'}>
      {!bare && <span className="text-xs font-medium text-muted">Year:</span>}
      <div className={bare ? 'flex items-center gap-0 p-0.5' : 'flex items-center gap-0 rounded-lg border border-border-primary bg-surface-elevated p-0.5'}>
        <button
          onClick={() => canPrev && setMonth(availableYears[effectiveIdx - 1], month)}
          disabled={!canPrev}
          aria-label={uiFb('ui.year.previous', 'Previous year', lang)}
          className="rounded-md px-2 py-1 text-xs font-medium text-muted transition-colors hover:bg-surface-hover hover:text-text-secondary disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <path d="M6.5 2 L3.5 5 L6.5 8" />
          </svg>
        </button>
        <span className="min-w-[3rem] text-center text-xs font-semibold tabular-nums text-text-primary">
          {year}
        </span>
        <button
          onClick={() => canNext && setMonth(availableYears[effectiveIdx + 1], month)}
          disabled={!canNext}
          aria-label={uiFb('ui.year.next', 'Next year', lang)}
          className="rounded-md px-2 py-1 text-xs font-medium text-muted transition-colors hover:bg-surface-hover hover:text-text-secondary disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <path d="M3.5 2 L6.5 5 L3.5 8" />
          </svg>
        </button>
      </div>
    </div>
  );
}
